import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import moment from 'moment-timezone'
import React from 'react'
import { useDispatch } from 'react-redux'
import AttachmentVerificationTooltip from 'src/components/AttachmentVerificationTooltip/AttachmentVerificationTooltip'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgLightningBolt from 'src/components/Svgs/SvgLightningBolt'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  AttachmentVerification,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'

import { RowStatus } from './statusForRow'

export interface AttachmentRowProps {
  attachment: ReceiptAttachmentModel
  status: RowStatus
  integrationName?: string
  integrationLogoSrc?: string
  deleteAttachment?: (attachmentId: string) => void
  onOpenRequestError?: () => boolean
}

const BADGE_ICON_SIZE = 16
const VERIFIED_GREEN = '#006e65'
const ALERT_RED = '#d5404a'

// Parse a human-friendly filename from the attachment's original URL.
// Falls back to "Receipt" when the URL is opaque (e.g. signed storage URL).
export function filenameFromAttachment(
  attachment: ReceiptAttachmentModel,
): string {
  const url = attachment?.urls?.original
  if (!url) return 'Receipt'
  try {
    const parsed = new URL(url)
    const last = parsed.pathname.split('/').filter(Boolean).pop()
    const decoded = last ? decodeURIComponent(last) : undefined
    if (decoded && /\.[a-zA-Z0-9]+$/.test(decoded)) return decoded
  } catch {
    /* fall through */
  }
  return 'Receipt'
}

function BadgeContent({
  status,
  integrationLogoSrc,
}: {
  status: RowStatus
  integrationLogoSrc?: string
}): React.ReactElement | null {
  switch (status.badge) {
    case 'verified':
      return (
        <SvgCheckFilled
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
          fill={VERIFIED_GREEN}
        />
      )
    case 'not-verified':
      return <span aria-hidden="true">{'!'}</span>
    case 'erp-success':
      return integrationLogoSrc ? (
        <img src={integrationLogoSrc} alt="" aria-hidden="true" />
      ) : (
        <SvgCheckFilled
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
          fill={VERIFIED_GREEN}
        />
      )
    case 'erp-failed':
      return (
        <SvgAlertCircledFilled
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
          fill={ALERT_RED}
        />
      )
    case 'auto-matched':
      return <SvgLightningBolt fill="#5a4fd4" />
    default:
      return null
  }
}

export default function AttachmentRow({
  attachment,
  status,
  integrationName,
  integrationLogoSrc,
  deleteAttachment,
  onOpenRequestError,
}: AttachmentRowProps): React.ReactElement {
  const translate = useTranslate()
  const dispatch = useDispatch()

  const filename = filenameFromAttachment(attachment)
  const timestamp = attachment.createdAt
    ? moment(attachment.createdAt).format('MMM D, h:mm A')
    : ''

  const statusText =
    status.statusReasons && status.statusReasons.length > 0
      ? status.statusReasons.map((r) => translate(r)).join(', ')
      : status.statusLabel.includes('${integration}')
        ? translate(status.statusLabel, { integration: integrationName ?? '' })
        : translate(status.statusLabel)

  const canDelete =
    deleteAttachment !== undefined && status.badge !== 'erp-success'

  const handleThumbClick = (): void => {
    dispatch(
      createSetIsAttachmentPopupOpen(
        true,
        canDelete
          ? (): void => {
              if (onOpenRequestError?.()) return
              deleteAttachment?.(attachment.id)
            }
          : undefined,
        attachment,
      ),
    )
  }

  const badgeClass =
    status.badge === 'verified'
      ? 'badge badge--verified'
      : status.badge === 'not-verified'
        ? 'badge badge--not-verified'
        : status.badge === 'erp-success'
          ? 'badge badge--erp-success'
          : status.badge === 'erp-failed'
            ? 'badge badge--erp-failed'
            : status.badge === 'auto-matched'
              ? 'badge badge--auto-matched'
              : undefined

  const rowClass = `attachment-row${
    status.textTone === 'error' ? ' row--error' : ''
  }`

  const thumbButton = (
    <button
      type="button"
      className="thumb-button"
      onClick={handleThumbClick}
      aria-label={translate('Preview ${filename}', { filename })}
      data-testid="attachmentRow-thumb"
    >
      <img src={attachment.urls.thumb} alt="" />
    </button>
  )

  const tooltipStatus =
    status.badge === 'verified' || status.badge === 'not-verified'
  const resolvedVerification = resolveAttachmentVerification(attachment)
  const wrappedThumb = tooltipStatus ? (
    <Tooltip
      placement="top"
      tooltipContent={
        <AttachmentVerificationTooltip
          status={resolvedVerification?.status}
          reasons={resolvedVerification?.reasons}
        />
      }
    >
      {thumbButton}
    </Tooltip>
  ) : (
    thumbButton
  )

  return (
    <div className={rowClass} data-testid="attachmentRow">
      <div className="row-content">
        <div className="thumb">
          {wrappedThumb}
          {badgeClass && (
            <span
              className={badgeClass}
              data-testid={`attachmentRow-badge-${status.badge}`}
            >
              <BadgeContent
                status={status}
                integrationLogoSrc={integrationLogoSrc}
              />
            </span>
          )}
        </div>
        <div className="text-block">
          <p className="filename" data-testid="attachmentRow-filename">
            {filename}
          </p>
          <p className="meta">
            {timestamp && (
              <span data-testid="attachmentRow-timestamp">{timestamp}</span>
            )}
            {timestamp && <span className="meta-sep">{'•'}</span>}
            <span data-testid="attachmentRow-status">{statusText}</span>
          </p>
          {status.showProgress && (
            <div
              className={`progress${
                status.progressTone === 'error' ? ' progress--error' : ''
              }`}
              role="progressbar"
              aria-label={statusText}
              data-testid="attachmentRow-progress"
            >
              <div className="progress-fill" />
            </div>
          )}
        </div>
      </div>
      {canDelete && (
        <button
          type="button"
          className="trash-btn"
          onClick={(): void => deleteAttachment?.(attachment.id)}
          aria-label={translate('Delete ${filename}', { filename })}
          data-testid="attachmentRow-delete"
        >
          <SvgClose />
        </button>
      )}
    </div>
  )
}

type Verification = AttachmentVerification
export type { Verification }
